import type {
  GalleryPreset,
  TemplateStructure,
  ThemeVariant,
  PageBlueprint,
  DemoDataset,
  PresetCategory,
  PresetFilterMetadata,
  ApplyMode,
  PresetUndoSnapshot,
} from "./types";
import type { ProfileData, SectionData } from "../types";
import type { ProductTier } from "@profolio/validators";
import { validatePresetRegistry } from "./validation";

export interface PresetQueryFilters {
  category?: PresetCategory;
  purpose?: PresetFilterMetadata["purpose"];
  aesthetic?: PresetFilterMetadata["aesthetic"];
  contentEmphasis?: PresetFilterMetadata["contentEmphasis"][number];
  userStage?: PresetFilterMetadata["userStage"];
  tier?: ProductTier;
  search?: string;
  isFeatured?: boolean;
}

export interface ResolvedPreset {
  preset: GalleryPreset;
  structure: TemplateStructure;
  theme: ThemeVariant;
  blueprint: PageBlueprint;
  demoDataset: DemoDataset;
}

export interface ApplyPresetInput {
  presetId: string;
  targetTier: ProductTier;
  currentProfile: ProfileData;
  currentSections?: SectionData[];
  mode?: ApplyMode;
  sectionsToAdd?: string[]; // IDs/types of sections to instantiate if missing
}

export interface ApplyPresetResult {
  updatedProfile: ProfileData;
  updatedSections?: SectionData[];
  undoSnapshot: PresetUndoSnapshot;
}

import { ALL_STRUCTURES } from "./structures";
import { ALL_THEMES } from "./themes";
import { ALL_BLUEPRINTS } from "./blueprints";
import { ALL_DEMO_DATASETS } from "./demo-data";
import { ALL_FLAGSHIP_PRESETS } from "./collections";

// ── In-Memory Registries ─────────────────────────────────────────────────────

const structuresRegistry = new Map<string, TemplateStructure>();
const themesRegistry = new Map<string, ThemeVariant>();
const blueprintsRegistry = new Map<string, PageBlueprint>();
const demoDatasetsRegistry = new Map<string, DemoDataset>();
const presetsRegistry = new Map<string, GalleryPreset>();

/**
 * Initializes and seeds the in-memory registry with all flagship architectural items and presets.
 */
export function initializeFlagshipRegistry(): void {
  ALL_STRUCTURES.forEach((s) => structuresRegistry.set(s.id, s));
  ALL_THEMES.forEach((t) => themesRegistry.set(t.id, t));
  ALL_BLUEPRINTS.forEach((b) => blueprintsRegistry.set(b.id, b));
  ALL_DEMO_DATASETS.forEach((d) => demoDatasetsRegistry.set(d.id, d));
  ALL_FLAGSHIP_PRESETS.forEach((p) => presetsRegistry.set(p.presetId, p));
}

/**
 * Resets registries to clean empty state (primarily used in test fixtures).
 */
export function resetRegistryForTesting(): void {
  structuresRegistry.clear();
  themesRegistry.clear();
  blueprintsRegistry.clear();
  demoDatasetsRegistry.clear();
  presetsRegistry.clear();
}

// Auto-seed registry on load
initializeFlagshipRegistry();

// ── Registration APIs ────────────────────────────────────────────────────────

export function registerStructure(structure: TemplateStructure): void {
  structuresRegistry.set(structure.id, structure);
}

export function registerThemeVariant(theme: ThemeVariant): void {
  themesRegistry.set(theme.id, theme);
}

export function registerPageBlueprint(blueprint: PageBlueprint): void {
  blueprintsRegistry.set(blueprint.id, blueprint);
}

export function registerDemoDataset(demoData: DemoDataset): void {
  demoDatasetsRegistry.set(demoData.id, demoData);
}

export function registerPreset(preset: GalleryPreset): void {
  presetsRegistry.set(preset.presetId, preset);
}

// ── Retrieval APIs ───────────────────────────────────────────────────────────

export function getStructure(id: string): TemplateStructure | undefined {
  return structuresRegistry.get(id);
}

export function getAllStructures(): TemplateStructure[] {
  return Array.from(structuresRegistry.values());
}

export function getThemeVariant(id: string): ThemeVariant | undefined {
  return themesRegistry.get(id);
}

export function getAllThemeVariants(): ThemeVariant[] {
  return Array.from(themesRegistry.values());
}

export function getPageBlueprint(id: string): PageBlueprint | undefined {
  return blueprintsRegistry.get(id);
}

export function getAllBlueprints(): PageBlueprint[] {
  return Array.from(blueprintsRegistry.values());
}

export function getDemoDataset(id: string): DemoDataset | undefined {
  return demoDatasetsRegistry.get(id);
}

export function getAllDemoDatasets(): DemoDataset[] {
  return Array.from(demoDatasetsRegistry.values());
}

export function getPreset(presetId: string): GalleryPreset | undefined {
  return presetsRegistry.get(presetId);
}

export const getPresetById = getPreset;

export function getAllPresets(): GalleryPreset[] {
  return Array.from(presetsRegistry.values());
}

/**
 * Resolves a gallery preset into its 4 constituent architectural objects.
 */
export function resolvePresetComposition(presetId: string): ResolvedPreset | null {
  const preset = presetsRegistry.get(presetId);
  if (!preset) return null;

  const structure = structuresRegistry.get(preset.structureId);
  const theme = themesRegistry.get(preset.themeId);
  const blueprint = blueprintsRegistry.get(preset.blueprintId);
  const demoDataset = demoDatasetsRegistry.get(preset.demoDataId);

  if (!structure || !theme || !blueprint || !demoDataset) {
    return null;
  }

  return {
    preset,
    structure,
    theme,
    blueprint,
    demoDataset,
  };
}

// ── Search & Filter Engine ───────────────────────────────────────────────────

export function queryPresets(filters: PresetQueryFilters = {}): GalleryPreset[] {
  return Array.from(presetsRegistry.values()).filter((preset) => {
    // 1. Category match
    if (filters.category && preset.category !== filters.category) {
      return false;
    }

    // 2. Featured match
    if (filters.isFeatured !== undefined && !!preset.isFeatured !== filters.isFeatured) {
      return false;
    }

    // 3. Faceted Filter: Purpose
    if (filters.purpose && preset.filters.purpose !== filters.purpose) {
      return false;
    }

    // 4. Faceted Filter: Aesthetic
    if (filters.aesthetic && preset.filters.aesthetic !== filters.aesthetic) {
      return false;
    }

    // 5. Faceted Filter: User Stage
    if (filters.userStage && preset.filters.userStage !== filters.userStage) {
      return false;
    }

    // 6. Faceted Filter: Content Emphasis
    if (
      filters.contentEmphasis &&
      !preset.filters.contentEmphasis.includes(filters.contentEmphasis)
    ) {
      return false;
    }

    // 7. Tier Filter: matches exact tier or lower requirement
    if (filters.tier) {
      const tierRank: Record<ProductTier, number> = {
        starter: 1,
        pro: 2,
        premium: 3,
      };
      if (tierRank[preset.requiredTier] > tierRank[filters.tier]) {
        return false;
      }
    }

    // 8. Search query match (name, tagline, audience)
    if (filters.search && filters.search.trim().length > 0) {
      const q = filters.search.toLowerCase().trim();
      const nameMatch = preset.name.toLowerCase().includes(q);
      const taglineMatch = preset.tagline.toLowerCase().includes(q);
      const audienceMatch = preset.intendedAudience.toLowerCase().includes(q);
      if (!nameMatch && !taglineMatch && !audienceMatch) {
        return false;
      }
    }

    return true;
  });
}

// ── Preset Application Contract ──────────────────────────────────────────────

/**
 * Applies a gallery preset to a profile draft non-destructively.
 * Generates an immutable updated state and an undo snapshot.
 */
export function applyPresetToProfile(input: ApplyPresetInput): ApplyPresetResult {
  const resolved = resolvePresetComposition(input.presetId);
  if (!resolved) {
    throw new Error(`Cannot apply preset: preset '${input.presetId}' is incomplete or not found.`);
  }

  const { preset, structure, theme, blueprint } = resolved;
  const mode = input.mode || "visual-only";

  // Capture pre-apply state for one-click undo
  const undoSnapshot: PresetUndoSnapshot = {
    appliedAt: new Date().toISOString(),
    presetId: preset.presetId,
    presetVersion: preset.presetVersion,
    previousProfileState: { ...input.currentProfile },
    previousSectionsState: input.currentSections ? [...input.currentSections] : undefined,
  };

  // 1. Expand concrete design values onto profile
  const FREE_THEMES = ["classic-academic", "minimalist-scholar"];
  const FREE_FONTS = ["serif-classic", "modern-sans"];
  const FREE_PALETTES = [
    "ocean-blue",
    "oxford-navy",
    "emerald-forest",
    "royal-amethyst",
    "crimson-slate",
    "amber-gold",
    "midnight-dark",
    "minimal-monochrome",
  ];

  let effectiveThemeKey = structure.themeKey;
  let effectiveColorPalette = theme.colorPalette;
  let effectiveFontPairing = theme.fontPairing;
  let effectiveBackgroundStyle = theme.backgroundStyle;
  let effectiveAnimationLevel = theme.animationLevel;
  let effectiveSectionArrangement = structure.layoutModel;
  let effectiveLayoutOverrides: any = {
    ...(input.currentProfile.layoutOverrides || {}),
    ...(theme.layoutOverrides || {}),
    heroLayout: structure.heroLayout,
    ...(structure.heroOverrides || {}),
    hero: {
      ...((input.currentProfile.layoutOverrides as any)?.hero || {}),
      ...((theme.layoutOverrides as any)?.hero || {}),
      heroLayout: structure.heroLayout,
      ...(structure.heroOverrides || {}),
    },
  };

  // If user is Starter tier, adapt Pro-tier presets to Starter-safe equivalents
  if (input.targetTier === "starter") {
    if (!FREE_THEMES.includes(effectiveThemeKey)) {
      effectiveThemeKey = structure.id === "editorial-split" ? "minimalist-scholar" : "classic-academic";
    }
    if (!FREE_PALETTES.includes(effectiveColorPalette)) {
      if (effectiveColorPalette.includes("sapphire") || effectiveColorPalette.includes("navy")) {
        effectiveColorPalette = "oxford-navy";
      } else if (effectiveColorPalette.includes("cyber") || effectiveColorPalette.includes("swiss") || effectiveColorPalette.includes("monochrome")) {
        effectiveColorPalette = "minimal-monochrome";
      } else if (effectiveColorPalette.includes("aurora") || effectiveColorPalette.includes("royal") || effectiveColorPalette.includes("amethyst")) {
        effectiveColorPalette = "royal-amethyst";
      } else if (effectiveColorPalette.includes("botanical") || effectiveColorPalette.includes("emerald")) {
        effectiveColorPalette = "emerald-forest";
      } else {
        effectiveColorPalette = "ocean-blue";
      }
    }
    if (!FREE_FONTS.includes(effectiveFontPairing)) {
      effectiveFontPairing = theme.fontPairing.includes("serif") ? "serif-classic" : "modern-sans";
    }
    effectiveBackgroundStyle = "solid";
    effectiveAnimationLevel = "subtle";
    effectiveSectionArrangement = "linear";
    // Strip section-level layout overrides that trigger Starter PRO_FEATURE_REQUIRED
    effectiveLayoutOverrides = {};
  }

  const updatedProfile: ProfileData = {
    ...input.currentProfile,
    themeKey: effectiveThemeKey,
    colorPalette: effectiveColorPalette,
    fontPairing: effectiveFontPairing,
    styleRecipe: theme.styleRecipe,
    backgroundStyle: effectiveBackgroundStyle,
    animationLevel: effectiveAnimationLevel,
    layoutDensity: theme.layoutDensity,
    cornerRadius: theme.cornerRadius,
    typographyScale: theme.typographyScale,
    sectionArrangement: effectiveSectionArrangement,
    layoutOverrides: effectiveLayoutOverrides,
    // Record immutable provenance
    lastAppliedPresetId: preset.presetId,
    lastAppliedPresetVersion: preset.presetVersion,
  };

  let updatedSections = input.currentSections;

  // 2. Section re-ordering (only if explicit design-and-organize mode)
  if (mode === "design-and-organize" && input.currentSections) {
    const tierAdaptation =
      blueprint.adaptations[input.targetTier] || blueprint.adaptations.starter;

    // Aggregate desired section order across pages in this tier
    const targetOrder: string[] = [];
    tierAdaptation.pages.forEach((page) => {
      page.defaultSectionOrder.forEach((sectionType) => {
        if (!targetOrder.includes(sectionType)) {
          targetOrder.push(sectionType);
        }
      });
    });

    // Reorder existing sections according to target order, keeping unrecognized sections at the end
    const reordered = [...input.currentSections].sort((a, b) => {
      const idxA = targetOrder.indexOf(a.type);
      const idxB = targetOrder.indexOf(b.type);
      if (idxA === -1 && idxB === -1) return 0;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });

    // If user opted to add missing sections from the checklist
    if (input.sectionsToAdd && input.sectionsToAdd.length > 0) {
      const existingTypes = new Set(reordered.map((s) => s.type));
      input.sectionsToAdd.forEach((typeToAdd) => {
        if (!existingTypes.has(typeToAdd)) {
          reordered.push({
            id: `sec-${typeToAdd}-${Date.now()}`,
            type: typeToAdd,
            visible: true,
            displayOrder: reordered.length,
            clientKey: `key-${typeToAdd}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            content: {
              customTitle: typeToAdd.charAt(0).toUpperCase() + typeToAdd.slice(1),
            },
          });
          existingTypes.add(typeToAdd);
        }
      });
    }

    updatedSections = reordered.map((sec, idx) => ({
      ...sec,
      displayOrder: idx,
    }));
  }

  return {
    updatedProfile,
    updatedSections,
    undoSnapshot,
  };
}

// ── Registry Integrity Check ────────────────────────────────────────────────

export function validateCurrentRegistry() {
  return validatePresetRegistry(Array.from(presetsRegistry.values()), {
    structures: structuresRegistry,
    themes: themesRegistry,
    blueprints: blueprintsRegistry,
    demoDatasets: demoDatasetsRegistry,
  });
}
